import React from 'react'
import { getClassNames } from '../../../_utils/classnames'
import { NotificationActionBuilder } from '@pushly/aqe/lib/components/notification-action-builder/notification-action-builder'
import { noop } from '../../../_utils/utils'
import { DomainDto } from '../../../dtos/domain'
import { PromiseableProp } from '../../../_utils/promiseable-prop'
import { NotificationActionModel } from '../../../models/notification/notification-action.model'
import { useDebouncer } from '../../../hooks/use-debouncer'
import { NotificationDefaultContentModel } from '../../../models/notification/notification-default-content.model'
import { MacroOptionType } from '@pushly/aqe/lib/components/macro-manager/macro-manager'
import { actionBuilderFromAgnosticNotificationAction, getNotificationActionOrDefault } from '@pushly/aqe/lib/utils'
import { Flex } from 'antd'

// Use AQE's types via inference to avoid the dual-package hazard
// where pufferfish's @pushly/models and AQE's bundled copy are treated
// as incompatible types by TypeScript due to protected class members.
type AqeActionBuilder = ReturnType<typeof actionBuilderFromAgnosticNotificationAction>
type AqeNotificationActionBuilderProps = React.ComponentProps<typeof NotificationActionBuilder>
type AqeDomain = AqeNotificationActionBuilderProps['domain']

/**
 * Converts a NotificationActionModel (pufferfish local) to an
 * AQE-compatible NotificationActionBuilder for the shared component.
 */
const toBuilder = (model: NotificationActionModel): AqeActionBuilder => {
    return actionBuilderFromAgnosticNotificationAction({
        ordinal: model.getOrdinal()!,
        type: model.getType(),
        id: model.getId(),
        label: model.getLabel()!,
        landingUrl: model.getLandingUrl(),
        usePrimaryLandingUrl: model.getUsePrimaryLandingUrl(),
        displayMeta: model.getDisplayMeta(),
    } as any)
}

/**
 * Converts an AQE NotificationActionBuilder back to a
 * NotificationActionModel (pufferfish local) for the parent model.
 */
const toModel = (builder: AqeActionBuilder): NotificationActionModel => {
    return NotificationActionModel.build({
        id: builder.id,
        ordinal: builder.ordinal,
        type: builder.type,
        label: builder.label,
        landingUrl: builder.landingUrl,
        usePrimaryLandingUrl: builder.usePrimaryLandingUrl,
        displayMeta: builder.displayMeta,
    })
}

const getCompiledActions = (
    domain: DomainDto | null,
    actions: NotificationActionModel[] | undefined,
    reset?: boolean,
): NotificationActionModel[] => {
    const compiled = reset ? [] : actions?.map((a) => a.clone()) ?? []

    // ensure each action has a display eid
    compiled.forEach((a) => {
        if (!a.getDisplayMeta()?.eid) {
            a.setDisplayMeta({ ...a.getDisplayMeta(), eid: Math.random().toString(36).slice(2) })
        }
    })

    if (reset || actions === undefined) {
        // Use AQE's getNotificationActionOrDefault which handles domain defaults.
        const domainForAqe = domain as unknown as AqeDomain | null

        if (!compiled.find((a) => a.getOrdinal() === 0)) {
            const primary = getNotificationActionOrDefault(domainForAqe, 0)
            compiled.push(toModel(primary))
        }

        if (!compiled.find((a) => a.getOrdinal() === 1)) {
            const secondary = getNotificationActionOrDefault(domainForAqe, 1)
            if (secondary) {
                compiled.push(toModel(secondary))
            }
        }
    }

    return compiled
}

interface INotificationActionsBuilder {
    domain: DomainDto | null
    value: NotificationDefaultContentModel
    onChange: (value: NotificationDefaultContentModel) => any
    loading?: boolean
    customMacroOptions?: PromiseableProp<any>
    secretsMacroOptions?: PromiseableProp<any>
    validMacroOptionTypes?: MacroOptionType[]
}

export const NotificationActionsBuilder = (props: INotificationActionsBuilder) => {
    const { value, onChange, domain } = props

    const [debounce] = useDebouncer(4)

    const workingModel = value.clone()
    const workingActions = getCompiledActions(domain, workingModel.getActions() ?? undefined)
    const builders = workingActions.map(toBuilder)

    const totalActions = workingActions.length

    const handleActionUpdate = (v: AqeActionBuilder) => {
        const actionIdx = workingActions.findIndex((a) => a.getOrdinal() === v.ordinal)
        if (actionIdx !== -1) {
            workingActions.splice(actionIdx, 1, toModel(v))
            workingModel.setActions(workingActions)
        }

        debounce(() => onChange(workingModel))
    }

    const handleActionAdd = (v: AqeActionBuilder) => {
        if (totalActions < 2) {
            const newModel = toModel(v)
            newModel.setOrdinal(totalActions === 0 ? 0 : 1)
            workingActions.push(newModel)
            workingModel.setActions(workingActions)

            onChange(workingModel)
        }
    }

    const handleActionRemove = (v: AqeActionBuilder) => {
        if (totalActions > 1) {
            const actionIdx = workingActions.findIndex((a) => a.getOrdinal() === v.ordinal)
            if (actionIdx !== -1) {
                workingActions.splice(actionIdx, 1)
                workingActions[0].setOrdinal(0)
                workingModel.setActions(workingActions)

                onChange(workingModel)
            }
        }
    }

    const domainForAqe = domain as unknown as AqeDomain

    return (
        <Flex className={getClassNames('notif-action-builder-collection')}>
            {builders.map((builder, idx) => (
                <NotificationActionBuilder
                    key={`action-${idx}`}
                    domain={domainForAqe}
                    enabled={true}
                    title={`Button ${idx + 1}`}
                    primaryLandingUrl={value.getLandingUrl()}
                    config={builder}
                    onChange={handleActionUpdate}
                    onRemove={handleActionRemove}
                    rootActions={builders}
                    macroTypes={['domain', 'device', 'location', 'ecomm', 'notification', 'secrets', 'custom']}
                    customMacroOptions={props.customMacroOptions}
                    secretsMacroOptions={props.secretsMacroOptions}
                    validMacroOptionTypes={props.validMacroOptionTypes}
                />
            ))}
            {workingActions.length < 2 && (
                <NotificationActionBuilder
                    domain={domainForAqe}
                    enabled={false}
                    title={`Button Placeholder`}
                    primaryLandingUrl={value.getLandingUrl()}
                    onChange={noop}
                    onAdd={handleActionAdd}
                    rootActions={builders}
                />
            )}
        </Flex>
    )
}

export default NotificationActionsBuilder
